import React from "react";
import "./../styles/global.css";

// Hooks
import { Routes, Route, Router } from "../utilities/router";
// Providers
import { AuthProvider } from "utilities/auth";
import { QueryClientProvider } from "utilities/db";
import { HelmetProvider } from "react-helmet-async";
import { ToastProvider } from "components/Toast";

// Pages
import Home from "./Home";
import AuthPage from "./auth";
import FirebaseActionPage from "./firebase-action";
import NotFoundPage from "./404";
import ContactLandingPage from "./Contact";

// Customer Pages
import Registration from "./Registration";
import Confirmation from "../components/Registration/Confirmation";
import ApplicationRejected from "./ApplicationRejected";

// Internal Pages
import Companies from "./companies/Companies";
import { CompanyDetails } from "./companies/CompanyDetails";
import MidApplications from "./mid-applications/MidApplications";
import MidApplication from "./mid-applications/MidApplication";
import FocusView from "./mid-applications/FocusView";
import MidApplicationCreate from "./mid-applications/MidApplicationCreate";
import Tickets from "./tickets/Tickets";
import Ticket from "./tickets/Ticket";
import MidPortfolio from "./mids/MidPortfolio";
import Files from "./files/Files";
import MidDetail from "./mids/MidDetail";
import MidCreate from "./mids/MidCreate";
import ProcessorList from "./processors/ProcessorList";
import ProcessorDetail from "./processors/ProcessorDetail";
import FunctionsRegistry from "./settings/FunctionsRegistry";
import AgentsRegistry from "./settings/AgentsRegistry";
import BanksRegistry from "./settings/BanksRegistry";
import NotificationPreferences from "./settings/NotificationPreferences";
import NotificationConfig from "./settings/NotificationConfig";
import NotificationEventEditor from "./settings/NotificationEventEditor";
import WorkflowList from "./settings/WorkflowList";
import WorkflowEditor from "./settings/WorkflowEditor";
import SlaPolicies from "./settings/SlaPolicies";
import RequestTypes from "./settings/RequestTypes";
import RequestTypeEditor from "./settings/RequestTypeEditor";
import SlaPolicyEditor from "./settings/SlaPolicyEditor";
import Workload from "./settings/Workload";
import Audit from "./Audit";
import AuditLogPage from "./AuditLogPage";
import Rules from "./Rules";
import RuleCreate from "./RuleCreate";
import RuleDetail from "./RuleDetail";
import RuleEdit from "./RuleEdit";
import BulkRuleCreate from "./BulkRuleCreate";


// Components
import ErrorBoundary from "components/ErrorBoundaries/ErrorBoundary";
import AccountLayout from "components/Layouts/AccountLayout";
import "../utilities/analytics";
import { RequireRole } from "components/Auth/RequireRole";
import { RequireSubmittedApplication } from "components/Auth/RequireSubmittedApplication";
import { useAuth } from "utilities/auth";
import { useAdminCompany } from "context/AdminCompanyContext";
import PortfolioDashboard from "./PortfolioDashboard";
import { Navigate } from "react-router-dom";
import RulesPage from "./RulesPage";
import RerunnerPage from "./rerunner/RerunnerPage";
import RerunnerCreate from "./rerunner/RerunnerCreate";
import RerunnerEdit from "./rerunner/RerunnerEdit";
import SettingsLayout from "components/Settings/SettingsLayout";
import {
  ApiKeysSection,
  WebhooksSection,
  TeamSection,
  TelegramSubscribersSection,
} from "./settings/sections";
import CredentialsPage from "./integrations/CredentialsPage";
import CredentialDetail from "./integrations/CredentialDetail";
import PipelineConfigPage from "./integrations/PipelineConfigPage";
import DisputifierPortfolio from "./disputifier/DisputifierPortfolio";
import DailyPerformance from "./analytics/DailyPerformance";
import RerunnerRecovery from "./analytics/RerunnerRecovery";
import AuthPerformance from "./analytics/AuthPerformance";
import RiskView from "./risk/RiskView";
import RoutingClients from "./routing/RoutingClients";
import RoutingClient from "./routing/RoutingClient";
import RoutingChangeSet from "./routing/RoutingChangeSet";
import AlertsPage from "./alerts/AlertsPage";
import LtvDecomposedView from "./ltv/LtvDecomposedView";
import BillingLayout from "components/Billing/BillingLayout";
import DisputifierInvoices from "./billing/DisputifierInvoices";
import ProfilePage from "./ProfilePage";
import PartnerAgentTerms from "./PartnerAgentTerms";
import Wiki from "./wiki/Wiki";
import { useParams } from "react-router-dom";
import { getUserType } from "utilities/userType";

/**
 * Redirect helper that preserves the splat suffix.
 * Maps /old/<rest> → /new/<rest> so bookmarked sub-paths still resolve.
 */
function PathRedirect({ to }) {
  const params = useParams();
  const suffix = params["*"];
  const tail = suffix ? `/${suffix}` : "";
  return <Navigate to={`${to}${tail}`} replace />;
}

function ClientDashboard() {
  const { user } = useAuth();
  const { activeCompanyId, isParentCompany, childCompanies } = useAdminCompany();

  if (isParentCompany && !activeCompanyId) {
    return <PortfolioDashboard childCompanies={childCompanies} />;
  }

  return <CompanyDetails companyId={activeCompanyId || user.companyId} />;
}

function Dashboard() {
  const { user } = useAuth();
  const userType = getUserType(user);

  // SOUND-293: partner portal consolidated into /companies.
  // Preserve bookmarks by redirecting partners hitting /dashboard.
  if (userType === "partner") return <Navigate to="/companies" replace />;
  return <ClientDashboard />;
}

/**
 * The "/" splash is the public marketing site. A signed-in processor
 * (SOUND-563) has no use for it — and both the nav logo and their post-login
 * redirect can land here — so send them to their monitoring home instead.
 * Everyone else (logged-out, or still-loading auth) sees the marketing page.
 */
function HomeRoute() {
  const { user } = useAuth();
  if (user && getUserType(user) === "processor") {
    return <Navigate to="/risk" replace />;
  }
  return <Home />;
}

function App(props) {
  return (
    <Router>
      <HelmetProvider>
        <QueryClientProvider>
          {/* SOUND-726: the OUTER boundary has to sit ABOVE AuthProvider.
              `useMergeExtraData` throws outright when the user query fails, and
              AuthProvider sat outside the only boundary we had — so that throw
              unmounted the entire tree and left a blank page with no fallback,
              no message and no way back. A boundary below the thing that throws
              catches nothing. */}
          <ErrorBoundary>
          <AuthProvider>
            <ToastProvider position="top-center" max={4}>
              <ErrorBoundary>
                <Routes>
                  {/* ========== HOME ========== */}
                  <Route path="/" element={<HomeRoute />} />

                  {/* ========== AUTH | SIGNUP | SIGNIN ========== */}
                  <Route path="/auth/:type" element={<AuthPage />} />
                  <Route
                    path="/registration"
                    element={
                      <RequireRole role="client">
                        <Registration />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="/registration-confirmation"
                    element={
                      <RequireRole role="client">
                        <Confirmation />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="/application-rejected"
                    element={
                      <RequireRole role="client">
                        <ApplicationRejected />
                      </RequireRole>
                    }
                  />

                  {/* ========== ACCOUNT LAYOUT WRAPPER ========== */}
                  <Route path="/" element={<AccountLayout />}>
                    {/* ===== Customer-facing ===== */}
                    {/* SOUND-726 (CPO): a merchant whose application is still
                        new or in progress has no business on the dashboard —
                        it reports on a live account and theirs does not exist
                        yet, so it reads as a broken product rather than a next
                        step. The gate sends them back to the wizard, and lets
                        partners, parent companies and anyone without an
                        application straight through. */}
                    <Route
                      path="/dashboard"
                      element={
                        <RequireRole role={["client", "partner"]}>
                          <RequireSubmittedApplication>
                            <Dashboard />
                          </RequireSubmittedApplication>
                        </RequireRole>
                      }
                    />
                    {/* --- Clients (CRM) --- */}
                    <Route
                      path="/companies"
                      element={
                        <RequireRole role={["internal", "partner"]}>
                          <Companies />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/companies/:id"
                      element={
                        <RequireRole role={["internal", "partner"]}>
                          <CompanyDetails />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/companies/:id/create"
                      element={
                        <RequireRole role="internal">
                          <MidApplicationCreate />
                        </RequireRole>
                      }
                    />

                    {/* --- MID Applications (CRM) --- */}
                    <Route
                      path="/mid-applications"
                      element={
                        <RequireRole role={["internal", "partner"]}>
                          <MidApplications />
                        </RequireRole>
                      }
                    />
                    {/* SOUND-690 — BEFORE /:id. React Router v6 ranks static
                        segments above dynamic ones so this would win either way,
                        but the adjacency is the documentation: "focus" must never
                        be parsed as an applicationId. */}
                    <Route
                      path="/mid-applications/focus"
                      element={
                        <RequireRole role={["internal"]}>
                          <FocusView />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/mid-applications/:id"
                      element={
                        <RequireRole role={["internal", "partner", "client"]}>
                          <MidApplication />
                        </RequireRole>
                      }
                    />

                    {/* --- Tickets (SOUND-493) — server-scoped + feature-gated
                        (requireFeature("ticketing")). All three audiences; the
                        page renders per-role (internal ops inbox / client
                        Support / partner attributed view). --- */}
                    <Route
                      path="/tickets"
                      element={
                        <RequireRole role={["internal", "partner", "client"]}>
                          <Tickets />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/tickets/:ticketId"
                      element={
                        <RequireRole role={["internal", "partner", "client"]}>
                          <Ticket />
                        </RequireRole>
                      }
                    />

                    {/* --- MID Manager (Portfolio) --- */}
                    <Route
                      path="/mids"
                      element={
                        <RequireRole role={["internal", "partner", "processor"]}>
                          <MidPortfolio />
                        </RequireRole>
                      }
                    />
                    {/* SOUND-826 — CheckoutChamp routing mirror, read-only.
                        INTERNAL ONLY: it shows every in-scope client's gateway
                        routing side by side. The API enforces it too
                        (requireRole("internal") on every /routing route). */}
                    <Route
                      path="/routing"
                      element={
                        <RequireRole role="internal">
                          <RoutingClients />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/routing/:companyId"
                      element={
                        <RequireRole role="internal">
                          <RoutingClient />
                        </RequireRole>
                      }
                    />
                    {/* SOUND-847 — Disputifier alert coverage by merchant and
                        MID. INTERNAL ONLY: it reads every merchant's alerts
                        side by side. The API enforces it too. */}
                    <Route
                      path="/alerts"
                      element={
                        <RequireRole role="internal">
                          <AlertsPage />
                        </RequireRole>
                      }
                    />
                    {/* SOUND-827 — review one routing change set; GO here
                        WRITES TO LIVE CheckoutChamp. Internal-only route; the
                        GO / Decline / Roll back buttons additionally require
                        role admin|super (canApproveRouting), and the API
                        enforces both. */}
                    <Route
                      path="/routing/:companyId/changes/:changeSetId"
                      element={
                        <RequireRole role="internal">
                          <RoutingChangeSet />
                        </RequireRole>
                      }
                    />
                    {/* SOUND-661 — cross-company document list. INTERNAL ONLY:
                        the only surface reading files outside one company, and
                        the collection holds driver's licences and personal bank
                        statements. Clients and partners have the per-company
                        Files tab, which is the same data correctly scoped. The
                        server enforces it too — `scope=all` is ignored for
                        non-internal callers. */}
                    <Route
                      path="/files"
                      element={
                        <RequireRole role={["internal"]}>
                          <Files />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/mids/create"
                      element={
                        <RequireRole role="internal">
                          <MidCreate />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/mids/:midId"
                      element={
                        <RequireRole role={["internal", "partner"]}>
                          <MidDetail />
                        </RequireRole>
                      }
                    />

                    {/* --- Processors (redirects to /settings/processors — SOUND-319) --- */}
                    <Route path="/processors" element={<Navigate to="/settings/processors" replace />} />
                    <Route path="/processors/*" element={<PathRedirect to="/settings/processors" />} />

                    {/* --- Wiki (SOUND-316) — no nav entry yet, accessible via URL only until SOUND-300 nav redesign ships --- */}
                    <Route
                      path="/wiki"
                      element={
                        <RequireRole role={["internal", "partner", "client"]}>
                          <Wiki />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/wiki/:slug"
                      element={
                        <RequireRole role={["internal", "partner", "client"]}>
                          <Wiki />
                        </RequireRole>
                      }
                    />

                    {/* --- Disputifier --- */}
                    <Route
                      path="/disputifier"
                      element={
                        <RequireRole role="internal">
                          <DisputifierPortfolio />
                        </RequireRole>
                      }
                    />

                    {/* --- Analytics (SOUND-410, SOUND-414, SOUND-590) --- */}
                    <Route
                      path="/analytics"
                      element={
                        <RequireRole role="internal">
                          <DailyPerformance />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/analytics/rerunner"
                      element={
                        <RequireRole role="internal">
                          <RerunnerRecovery />
                        </RequireRole>
                      }
                    />
                    {/* Authorization Performance report builder (SOUND-590) —
                        analyst-driven GROUP BY over mart_auth_performance. */}
                    <Route
                      path="/analytics/auth"
                      element={
                        <RequireRole role="internal">
                          <AuthPerformance />
                        </RequireRole>
                      }
                    />
                    {/* Risk — internal + processor (SOUND-563). Processors are
                        server-pinned to their own MIDs; this is their home. */}
                    <Route
                      path="/risk"
                      element={
                        <RequireRole role={["internal", "processor"]}>
                          <RiskView />
                        </RequireRole>
                      }
                    />
                    {/* --- LTV (SOUND-811/812) — internal-only. The decomposed
                        model over MART_LTV_COHORT_V2; the v1 modeler was retired
                        from the front end in SOUND-837. In the internal nav
                        (Analytics). --- */}
                    <Route
                      path="/ltv"
                      element={
                        <RequireRole role="internal">
                          <LtvDecomposedView />
                        </RequireRole>
                      }
                    />

                    {/* --- Billing (SOUND-447) — internal-only AR view with
                        provider subnav. Layout mirrors SettingsLayout; index
                        redirects to the first provider (Disputifier). --- */}
                    <Route
                      path="/billing"
                      element={
                        <RequireRole role="internal">
                          <BillingLayout />
                        </RequireRole>
                      }
                    >
                      <Route
                        index
                        element={<Navigate to="/billing/disputifier" replace />}
                      />
                      <Route path="disputifier" element={<DisputifierInvoices />} />
                    </Route>

                    {/* --- single pages --- */}
                    <Route
                      path="/rules"
                      element={
                        <RequireRole role="internal">
                          <RulesPage />
                        </RequireRole>
                      }
                    >
                      <Route index element={<Rules />} />
                    </Route>
                    <Route
                      path="/rules/create"
                      element={
                        <RequireRole role="internal">
                          <RuleCreate />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/rules/bulk-create"
                      element={
                        <RequireRole role="internal">
                          <BulkRuleCreate />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/rules/:ruleId"
                      element={
                        <RequireRole role="internal">
                          <RuleDetail />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/rules/:ruleId/edit"
                      element={
                        <RequireRole role="internal">
                          <RuleEdit />
                        </RequireRole>
                      }
                    />

                    {/* --- Change Log (SOUND-681) — promoted out of the Settings
                        rail into the CRM section of the primary nav and renamed:
                        it carries configuration changes, not just audit history.
                        The PATH deliberately stays /audit. Six call sites already
                        navigate to /audit/:logId (AuditLogCard, useCreateAuditLog,
                        AuditLogPage's parent link, RuleDetail x2, the company
                        ActivityTab), so owning the route here REMOVES the redirect
                        hop they were taking through /settings/audit rather than
                        adding one. Renaming to /change-log would break those links
                        and every existing bookmark for no gain. --- */}
                    <Route
                      path="/audit"
                      element={
                        <RequireRole role="internal">
                          <Audit />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/audit/:logId"
                      element={
                        <RequireRole role="internal">
                          <AuditLogPage />
                        </RequireRole>
                      }
                    />

                    {/* --- Rerunner --- */}
                    <Route
                      path="/rerunner"
                      element={
                        <RequireRole role={["internal", "client"]}>
                          <RerunnerPage />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/rerunner/create"
                      element={
                        <RequireRole role="internal">
                          <RerunnerCreate />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/rerunner/:clientId/edit"
                      element={
                        <RequireRole role={["internal", "client"]}>
                          <RerunnerEdit />
                        </RequireRole>
                      }
                    />

                    {/* --- Settings (SOUND-319) — left-rail layout with nested sections --- */}
                    {/* Layout-level guard allows all authenticated users; per-section
                        guards below restrict internal-only sections. Clients
                        currently see only Profile (sidebar filters per user type). */}
                    <Route
                      path="/settings"
                      element={
                        <RequireRole role={["internal", "partner", "client"]}>
                          <SettingsLayout />
                        </RequireRole>
                      }
                    >
                      <Route index element={<Navigate to="/settings/profile" replace />} />
                      <Route path="profile" element={<ProfilePage />} />
                      {/* Per-user notification preferences (SOUND-515) — internal
                          only (matches the bell); the prefs catalog is inert for
                          clients today. Reopen with audience-filtering later. */}
                      <Route path="notifications" element={<RequireRole role="internal"><NotificationPreferences /></RequireRole>} />
                      {/* Notification ADMIN config — routing + template authoring
                          (SOUND-523), internal-only. Distinct from the per-user
                          prefs at /settings/notifications. */}
                      <Route path="notification-config" element={<RequireRole role="internal"><NotificationConfig /></RequireRole>} />
                      <Route path="notification-config/:eventKey" element={<RequireRole role="internal"><NotificationEventEditor /></RequireRole>} />
                      <Route path="api-keys" element={<ApiKeysSection />} />
                      <Route path="webhooks" element={<WebhooksSection />} />
                      <Route path="team" element={<TeamSection />} />

                      {/* Telegram — internal only. Commands tab retired
                          (SOUND-530, commandResponses is dead) and Templates tab
                          retired (SOUND-523, authoring moved to
                          /settings/notification-config); both old paths redirect
                          to Subscribers, the default landing. */}
                      <Route path="telegram" element={<RequireRole role="internal"><Navigate to="/settings/telegram/subscribers" replace /></RequireRole>} />
                      <Route path="telegram/commands" element={<Navigate to="/settings/telegram/subscribers" replace />} />
                      <Route path="telegram/commands/*" element={<Navigate to="/settings/telegram/subscribers" replace />} />
                      <Route path="telegram/templates" element={<Navigate to="/settings/telegram/subscribers" replace />} />
                      <Route path="telegram/templates/*" element={<Navigate to="/settings/telegram/subscribers" replace />} />
                      <Route path="telegram/subscribers" element={<RequireRole role="internal"><TelegramSubscribersSection /></RequireRole>} />
                      {/* SOUND-247: telegram/notifications removed — it held an ungated broadcast-to-all. */}
                      <Route path="telegram/notifications" element={<Navigate to="/settings/telegram/subscribers" replace />} />

                      {/* Audit — moved OUT to /audit as "Change Log" (SOUND-681).
                          Redirects kept so bookmarks made while it lived in this
                          rail still land; this is the exact reverse of the
                          SOUND-319 pair that used to point the other way. */}
                      <Route path="audit" element={<Navigate to="/audit" replace />} />
                      <Route path="audit/*" element={<PathRedirect to="/audit" />} />

                      {/* Processors — internal only */}
                      <Route path="processors" element={<RequireRole role="internal"><ProcessorList /></RequireRole>} />
                      <Route path="processors/new" element={<RequireRole role="internal"><ProcessorDetail /></RequireRole>} />
                      <Route path="processors/:processorId" element={<RequireRole role="internal"><ProcessorDetail /></RequireRole>} />

                      {/* Agent + Bank registries (SOUND-672) — internal only.
                          The counterparties either side of a processor in the
                          AGENT -> PROCESSOR -> SPONSOR BANK chain. Four fields
                          each, so these are single list+modal routes rather
                          than the list/new/:id triad Processors carries. */}
                      <Route path="agents" element={<RequireRole role="internal"><AgentsRegistry /></RequireRole>} />
                      <Route path="banks" element={<RequireRole role="internal"><BanksRegistry /></RequireRole>} />

                      {/* Job Functions registry (SOUND-504) — internal only */}
                      <Route path="functions" element={<RequireRole role="internal"><FunctionsRegistry /></RequireRole>} />

                      {/* Ticket workflow builder (SOUND-506) — internal only.
                          list / new / :templateId triad, mirrors Processors. */}
                      <Route path="workflows" element={<RequireRole role="internal"><WorkflowList /></RequireRole>} />
                      <Route path="workflows/new" element={<RequireRole role="internal"><WorkflowEditor /></RequireRole>} />
                      <Route path="workflows/:templateId" element={<RequireRole role="internal"><WorkflowEditor /></RequireRole>} />

                      {/* Request types (SOUND-544) — internal only. list / new /
                          :key triad; the key is the immutable join key. */}
                      <Route path="request-types" element={<RequireRole role="internal"><RequestTypes /></RequireRole>} />
                      <Route path="request-types/new" element={<RequireRole role="internal"><RequestTypeEditor /></RequireRole>} />
                      <Route path="request-types/:key" element={<RequireRole role="internal"><RequestTypeEditor /></RequireRole>} />

                      {/* SLA policies (SOUND-534) — internal only. list / new /
                          :policyId triad, mirrors Workflows. */}
                      <Route path="sla-policies" element={<RequireRole role="internal"><SlaPolicies /></RequireRole>} />
                      <Route path="sla-policies/new" element={<RequireRole role="internal"><SlaPolicyEditor /></RequireRole>} />
                      <Route path="sla-policies/:policyId" element={<RequireRole role="internal"><SlaPolicyEditor /></RequireRole>} />

                      {/* Workload / resource dashboard (SOUND-538) — internal only. */}
                      <Route path="workload" element={<RequireRole role="internal"><Workload /></RequireRole>} />
                    </Route>

                    {/* --- Profile (redirect to /settings/profile — SOUND-319) --- */}
                    <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />

                    {/* --- Partner Agent T&C (SOUND-276) --- */}
                    <Route
                      path="/partner-agent-terms"
                      element={
                        <RequireRole role={["internal", "client", "partner"]}>
                          <PartnerAgentTerms />
                        </RequireRole>
                      }
                    />

                    {/* --- Credentials (SOUND-709) ---
                        Was /integrations holding Credentials + Pipeline Config.
                        SOUND-681 had already dissolved the Integrations SECTION
                        and relabelled this route "Credentials" for internal
                        users while clients still read "Integrations" — same URL,
                        two names. Pipeline Config moved to the Settings rail,
                        so the route now holds only what it is named after.

                        PARTNER IS NOT IN THE ROLE LIST. CPO 2026-07-30:
                        partners can never see client stored credentials, not
                        the values and not the RECORDS. This is the third
                        independent refusal, after the `credentials` flag (not
                        granted to partners) and credentialScope()'s no-match
                        filter. */}
                    <Route
                      path="/credentials"
                      element={
                        <RequireRole role={["internal", "client"]}>
                          <CredentialsPage />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/credentials/:credentialId"
                      element={
                        <RequireRole role={["internal", "client"]}>
                          <CredentialDetail />
                        </RequireRole>
                      }
                    />
                    {/* Old path kept as a redirect. Only four references pointed
                        here and none were deep links, so unlike /audit in
                        SOUND-681 the rename was cheap — but bookmarks are free
                        to honour. */}
                    <Route path="/integrations" element={<Navigate to="/credentials" replace />} />

                    {/* --- Pipeline Config (SOUND-709) — moved out of the
                        credentials route into the Settings rail, beside
                        Rerunner and Billing. Internal only. --- */}
                    <Route
                      path="/settings/pipeline-config"
                      element={
                        <RequireRole role={["internal"]}>
                          <PipelineConfigPage />
                        </RequireRole>
                      }
                    />

                    {/* --- Telegram (redirects to /settings/telegram — SOUND-319) --- */}
                    <Route path="/telegram" element={<Navigate to="/settings/telegram" replace />} />
                    <Route path="/telegram/*" element={<PathRedirect to="/settings/telegram" />} />
                  </Route>

                  {/* ========== UTILITIES | PAYMENTS | 404 ========== */}

                  <Route path="/contact" element={<ContactLandingPage />} />
                  <Route
                    path="/firebase-action"
                    element={<FirebaseActionPage />}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </ErrorBoundary>
            </ToastProvider>
          </AuthProvider>
          </ErrorBoundary>
        </QueryClientProvider>
      </HelmetProvider>
    </Router>
  );
}

export default App;
